/**
 * Live chat dictation, the pure parts (MAR-1423). `app/composables/useLiveDictation.ts` drives the
 * microphone and the socket; everything here is arithmetic, so it is tested on its own
 * (`tests/unit/dictationPcm.test.ts`).
 *
 * Gemini Live's transcription model takes raw 16-bit PCM, 16 kHz, mono, little-endian, in chunks of
 * about 100 ms. The server reads any frame shorter than 256 bytes as a control message
 * (`server/services/dictation/liveRelay.ts`), so audio is only ever sent as whole 3,200-byte chunks.
 */

export const SAMPLE_RATE = 16_000
/** 100 ms of 16 kHz mono 16-bit audio. */
export const CHUNK_SAMPLES = 1_600
export const CHUNK_BYTES = CHUNK_SAMPLES * 2

/** One dictation's longest run (Alex, 2026-10-28). The gateway enforces it too. */
export const DICTATION_MAX_MS = 10 * 60 * 1000
/** A dictation stops after this long without speech (Alex, 2026-10-28). */
export const SILENCE_STOP_MS = 8_000
/** Below this RMS a 100 ms chunk counts as silence. Speech at a laptop mic sits well above it. */
export const SILENCE_RMS = 0.01

export type DictationMode = 'live' | 'browser' | 'none'

/**
 * Which mic the composer offers: live (Gemini, through the gateway) when the seat's plan has
 * `voice_transcription` and the browser can capture audio; otherwise today's browser
 * `SpeechRecognition`, unchanged; otherwise none. The live path falls back to the browser one at
 * runtime when its connection cannot open.
 */
export function dictationMode(opts: { voiceTranscription: boolean, browserSpeech: boolean, microphone: boolean }): DictationMode {
  if (opts.voiceTranscription && opts.microphone) return 'live'
  return opts.browserSpeech ? 'browser' : 'none'
}

/** Float samples at `inputRate` → 16 kHz signed 16-bit, averaging each output sample's window. */
export function downsampleToInt16(input: Float32Array, inputRate: number): Int16Array {
  const ratio = inputRate / SAMPLE_RATE
  const length = Math.floor(input.length / ratio)
  const out = new Int16Array(length)
  for (let i = 0; i < length; i++) {
    const from = Math.floor(i * ratio)
    const to = Math.max(from + 1, Math.floor((i + 1) * ratio))
    let sum = 0
    for (let j = from; j < to; j++) sum += input[j]!
    const s = Math.max(-1, Math.min(1, sum / (to - from)))
    out[i] = Math.round(s < 0 ? s * 32768 : s * 32767)
  }
  return out
}

/** Collects samples and hands out whole 100 ms chunks as little-endian bytes. */
export class PcmChunker {
  private buffer = new Int16Array(CHUNK_SAMPLES)
  private filled = 0

  push(samples: Int16Array): Uint8Array[] {
    const chunks: Uint8Array[] = []
    let offset = 0
    while (offset < samples.length) {
      const take = Math.min(CHUNK_SAMPLES - this.filled, samples.length - offset)
      this.buffer.set(samples.subarray(offset, offset + take), this.filled)
      this.filled += take
      offset += take
      if (this.filled === CHUNK_SAMPLES) {
        const bytes = new Uint8Array(CHUNK_BYTES)
        const view = new DataView(bytes.buffer)
        for (let i = 0; i < CHUNK_SAMPLES; i++) view.setInt16(i * 2, this.buffer[i]!, true)
        chunks.push(bytes)
        this.filled = 0
      }
    }
    return chunks
  }
}

export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (const s of samples) sum += s * s
  return Math.sqrt(sum / samples.length)
}

/** The loudness (RMS, 0..1) of one little-endian 16-bit chunk. */
export function chunkLevel(chunk: Uint8Array): number {
  const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength)
  const samples = Math.floor(chunk.byteLength / 2)
  if (samples === 0) return 0
  let sum = 0
  for (let i = 0; i < samples; i++) {
    const s = view.getInt16(i * 2, true) / 32768
    sum += s * s
  }
  return Math.sqrt(sum / samples)
}

/** About this long without speech closes a turn, so Gemini finalises what was said. */
export const PAUSE_MS = 1_000

/**
 * Turns, from the client's side. The gateway opens a Gemini turn on the first audio it receives and
 * closes it on `{"type":"pause"}` (Gemini's own voice detection is off — `services/llm-gateway/src/live.rs`,
 * "Turns"), so the client decides both: no audio is sent until someone speaks, a pause of `PAUSE_MS`
 * after speech is announced once, and quiet audio stops flowing until speech returns — then the last
 * quiet chunk goes first, so the onset of the first word is not clipped. Quiet audio is never sent
 * for nothing, which is also audio nobody is billed for.
 */
export class TurnTracker {
  private speaking = false
  private lastLoud = 0
  private leadIn: Uint8Array | null = null

  push(chunk: Uint8Array, level: number, now: number): { send: Uint8Array[], pause: boolean } {
    const loud = level >= SILENCE_RMS
    if (!this.speaking) {
      if (!loud) {
        this.leadIn = chunk
        return { send: [], pause: false }
      }
      this.speaking = true
      this.lastLoud = now
      const send = this.leadIn ? [this.leadIn, chunk] : [chunk]
      this.leadIn = null
      return { send, pause: false }
    }
    if (loud) this.lastLoud = now
    if (!loud && now - this.lastLoud >= PAUSE_MS) {
      this.speaking = false
      return { send: [chunk], pause: true }
    }
    return { send: [chunk], pause: false }
  }
}

/** True once `SILENCE_STOP_MS` have passed since the last chunk louder than `SILENCE_RMS`. */
export class SilenceWatch {
  private lastSpeech: number
  constructor(startedAt: number, private readonly limitMs = SILENCE_STOP_MS) {
    this.lastSpeech = startedAt
  }

  observe(level: number, now: number): boolean {
    if (level >= SILENCE_RMS) this.lastSpeech = now
    return now - this.lastSpeech > this.limitMs
  }

  /** A transcript arriving is speech too, whatever the level said. */
  heard(now: number): void {
    this.lastSpeech = now
  }
}

/** What the composer shows: what was typed before, the finished utterances, and the live interim. */
export function composeDictation(base: string, finals: readonly string[], interim: string): string {
  return [base, ...finals, interim].map(part => part.trim()).filter(Boolean).join(' ')
}
